import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import * as yup from 'yup';
import { useFormik } from 'formik';
import api from '../../../../../components/api';

interface Driver {
  position: string
  firstname: string
  lastname: string
  phoneNumber: number
  deleted: 0 | 1
  username: string
  id: number
}

function useRoutesAddRoute() {
  /* detect id from params to do edit mode or add mode */
  const { id } = useParams();
  const navigation = useNavigate();

  // 1. Get Route List
  const { data: routeData, isLoading: routeLoading } = useQuery(
    ['getAll', 'routes'],
    () => api.routes.getAll(),
  );
  const routeList = React.useMemo(() => {
    const resData = routeData?.data?.data;
    if (resData) {
      return resData.map((route: any) => {
        const {
          name, id: routeId, user, clients,
        } = route;
        return {
          value: routeId,
          label: name,
          driverId: user?.id ?? '',
          clients: clients?.map((client: {id:string}) => client.id) ?? [],
        };
      });
    }
    return undefined;
  }, [routeData]);

  /***/
  /***/
  /***/

  /* This is the start of fetching initial data that are
  needed for list on form select like driverList and RouteList
  and initial data when this screen on edit mode */

  // 2. Get Driver list
  const { data: driverData, isLoading: driverLoading, error: driverError } = useQuery(
    ['getAllDrivers', 'routes'],
    () => api.routes.getAllDrivers(),
  );
  const driverList = React.useMemo(() => {
    const resData = driverData?.data?.data;
    if (resData) {
      return resData.map((driver: Driver) => {
        const { firstname, lastname, id: driverId } = driver;
        return {
          value: driverId,
          label: `${firstname} ${lastname}`,
        };
      });
    }
    return undefined;
  }, [driverData]);

  // 3. Get Schedule By Id (in case we are on edit mode)
  const { data: scheduleData, refetch: refectDetail } = useQuery(
    ['getById', 'schedules', id],
    () => api.schedules.getById(id!),
    {
      enabled: !!id,
    },
  );

  /* API management to add new Schedule to Database */
  const { isLoading: loadingSchedule, mutate: mutateSchedule } = useMutation(api.schedules.add, {
    onMutate: (x) => console.log('onMutate', x),
    onSuccess: (res) => {
      console.log(res);
      alert('success');
      navigation('/routes/schedule');
    },
    onError: (err) => {
      console.log('err', err);
      alert('error');
    },
  });

  /* API management to add client to Schedule */
  const { isLoading: addClientScheduleLoad, mutate: addClientSchedule } = useMutation(api.schedules.addClientToSchedule, {
    onMutate: (x) => console.log('onMutate', x), // showing data that are you sending
    onSuccess: (res) => {
      console.log(res);
      alert('success');
      navigation('/routes/schedule');
    },
    onError: (err) => {
      console.log('err', err);
      alert('error');
    },
  });

  /* form validation */
  /* change required function for showing error massage like you want */
  const yupSchemaAdd = yup.object({
    routeId: yup.string().required(),
    scheduleDate: yup.string().required(),
    userId: yup.number().required(),
  });

  /* form handler */
  const formik = useFormik({
    initialValues: {
      routeId: id ?? '',
      scheduleDate: `${new Date().getMonth() + 1}/${new Date().getDate()}/${new Date().getFullYear()}`,
      userId: '',
      sendEmail: true,
      sendSms: true,
      clients: [''],
    },
    validationSchema: yupSchemaAdd,
    onSubmit: (values) => {
      if (!id) {
        // add schedule
        mutateSchedule(values);
      } else {
        // update schedule
        const firstArr = scheduleData?.data?.data?.clients?.map((x: any) => x.id);
        const thirdArr = formik.values.clients;
        addClientSchedule({
          id: Number(id),
          clients: thirdArr.filter((x) => !firstArr?.includes(x)).map((y) => Number(y)),
        });
      }
    },
  });

  /* API management to update driver */
  const { isLoading: updateDriverLoad, mutate: updateDriver } = useMutation(api.schedules.updateDriver, {
    onMutate: (x) => console.log('onMutate', x), // showing data that are you sending
    onSuccess: (res) => {
      console.log(res);
      alert('success');
      refectDetail().catch(() => alert('error'));
    },
    onError: (err) => {
      console.log('err', err);
      alert('error');
    },
  });
  const handleUpdateDriver = () => {
    console.log(formik.values.scheduleDate);
    if (id) {
      updateDriver({
        id: Number(id),
        userId: Number(formik.values.userId),
        scheduleDate: formik.values.scheduleDate,
      });
    }
  };

  React.useEffect(() => {
    const getByIdData = scheduleData?.data?.data;

    if (getByIdData && id && routeList && driverList) {
      const findRoute = routeList.find((x: {value: number, label: string}) => x.label === getByIdData.name);
      const scheduleDate = getByIdData.date ? `${getByIdData.date}T00:00:00.000` : '';
      formik.setFieldValue('routeId', findRoute?.value ?? '');
      formik.setFieldValue('scheduleDate', scheduleDate ?? '');
      formik.setFieldValue('userId', getByIdData?.user?.id ?? '');
      const clientsId = getByIdData?.clients?.map((x: any) => x.id);
      formik.setFieldValue('clients', clientsId ?? []);
    }
  }, [scheduleData, routeList, driverList]);

  return {
    id,
    driverList,
    driverLoading,
    driverError,
    formik,
    isLoading: loadingSchedule || addClientScheduleLoad,
    routeList,
    routeLoading,
    updateDriver: {
      submit: handleUpdateDriver,
      load: updateDriverLoad,
    },
  };
}

export default useRoutesAddRoute;
